import './SignUp.css'
import { useEffect, useState } from 'react';
import { Header } from 'components/common/header/Header'
import Svgs from 'assets/svgs';
import { useQuery } from 'hooks/useQuery'
import { getStoredServiceId } from "common/app";
import { Device } from "common/device";
import { useAtomValue, useSetAtom, getDefaultStore } from 'jotai'
import { stepAtom, signUpDataAtom, popStepAtom, stepStackAtom, signUpHistoryDepthAtom, isExitingSignUp, setSignUpExiting, initialSignUpData } from "store/signUpStore";
import { useTranslation } from "react-i18next";
import { SignUpStep1 } from "components/signUp/SignUpStep1";
import { SignUpStep2 } from 'components/signUp/SignUpStep2';
import { SignUpStep3 } from 'components/signUp/SignUpStep3';
import { SignUpStep4 } from 'components/signUp/SignUpStep4';
import { SignUpStep5 } from 'components/signUp/SignUpStep5';
import { SignUpStep6 } from 'components/signUp/SignUpStep6';
import { SignUpStep7 } from 'components/signUp/SignUpStep7';
import { SignUpStep8 } from 'components/signUp/SignUpStep8';
import { SignUpStep9 } from 'components/signUp/SignUpStep9';
import { SignUpStep10 } from 'components/signUp/SignUpStep10';
import { SignUpStep11 } from 'components/signUp/SignUpStep11';
import { SignUpStep12 } from 'components/signUp/SignUpStep12';
import { userStore } from 'store/userStore';
import { ConfirmPopup } from 'wiz-front-shared/components/confirmPopup/ConfirmPopup';
import { useBackInterceptor } from 'hooks/useBackInterceptor';
import { requestApp, WEB_TO_APP } from 'common/webview';

export const SignUp = () => {
    const {t} = useTranslation();
    const { navigate } = useQuery();
    const step = useAtomValue(stepAtom);
    const popStep = useSetAtom(popStepAtom);
    const historyDepth = useAtomValue(signUpHistoryDepthAtom);

    // Step6(본인인증 통과 후 계정정보 입력 단계) 이탈 방지 팝업.
    //   device back 은 useBackInterceptor 가 메시지 기반으로 가로채므로 webview history 의존성 없음.
    const [abortOpen, setAbortOpen] = useState(false);

    // Step7 (이미 가입된 계정) → 로그인 이동 후 device back 으로 돌아온 경우 /signin 으로 redirect.
    //   pushState 로 쌓인 모든 sign-up entry 를 한번에 pop 해서 stale 회원가입 화면 노출 차단.
    const [redirectToSignIn] = useState(() => {
        if (typeof window === "undefined") return false;
        if (sessionStorage.getItem("__signupToSignIn") === "1") {
            sessionStorage.removeItem("__signupToSignIn");
            return true;
        }
        return false;
    });

    // 진입 시 atom 동기 reset — 자식 첫 render 전에 실행 (useEffect 는 race).
    //   - 일반 가입: 모든 필드 초기화
    //   - SNS 가입 진행중 (snsLogin && providerUserId): SNS 가 inject 한 필드만 보존, user-input 은 초기화
    //   - termsConsents, parentConsent 등 체크박스 상태도 매 진입 fresh
    useState(() => {
        if (redirectToSignIn) return null;
        const store = getDefaultStore();
        const prev = store.get(signUpDataAtom);
        const isSnsActive = prev.snsLogin && !!prev.providerUserId;

        // KR 다날 본인인증은 genderDigit(주민번호 뒷자리 첫 숫자) 계산이 필요해 OTHER 사용 불가.
        //   SNS 가 gender="OTHER" 를 보낸 KR 사용자는 값을 비워 Step2 에서 직접 선택하게 한다
        //   (라디오 옵션에 OTHER 가 없어 선택 표시조차 안 되고, 그대로 두면 다날 호출이 실패).
        const finalCountryCd = Device.countryCd || prev.countryCd;
        const krOtherGenderFromSns = isSnsActive
            && prev.snsProvidedFields.gender
            && finalCountryCd === "KR"
            && prev.gender === "OTHER";

        store.set(signUpDataAtom, {
            ...initialSignUpData,
            // SNS 가입 진행중이면 SNS 관련 필드 보존
            ...(isSnsActive ? {
                snsLogin: true,
                provider: prev.provider,
                providerUserId: prev.providerUserId,
                snsProvidedFields: prev.snsProvidedFields,
                // SNS 가 제공한 user 정보만 유지
                email: prev.snsProvidedFields.email ? prev.email : "",
                name: prev.snsProvidedFields.name ? prev.name : "",
                // 닉네임은 snsProvidedFields 추적 대상이 아니라 SNS 제공 시 항상 prefill.
                //   6자 초과여도 자르지 않고 원본 그대로 채운다 → Step6 검증이 길이 오류를 노출하고
                //   가입 버튼이 비활성화되어, 사용자가 스스로 줄이도록 유도 (임의 truncation 으로 인한 혼란 방지).
                nickname: prev.nickname,
                birth: prev.snsProvidedFields.birth ? prev.birth : "",
                // KR + SNS gender=OTHER → gender 도 비워서 사용자가 처음부터 선택하도록.
                gender: (prev.snsProvidedFields.gender && !krOtherGenderFromSns) ? prev.gender : "",
                // ctn / ctnCountryCd / ctnCountry 는 한 묶음 — Step3·Step4 prefill 에 함께 쓰인다.
                ctn: prev.snsProvidedFields.ctn ? prev.ctn : "",
                ctnCountryCd: prev.snsProvidedFields.ctn ? prev.ctnCountryCd : "",
                ctnCountry: prev.snsProvidedFields.ctn ? prev.ctnCountry : "",
                // SNS 프로필 이미지 — URL 은 보존하되 사용 여부는 매 진입 시 기본값으로 되돌린다
                //   (Step6 에서 다시 선택하게 되므로).
                snsProfileImgUrl: prev.snsProfileImgUrl,
                useSnsProfileImg: true,
            } : {}),
            // device / serviceId 정보 (항상 최신화)
            serviceId: getStoredServiceId(),
            deviceId: Device.deviceId,
            os: Device.os || "etc",
            locale: Device.language,
            countryCd: Device.countryCd,
        });

        store.set(stepStackAtom, [1]);
        setSignUpExiting(false);
        return null;
    });

    // 가입 연령 정책 적용 여부 조회 (진입 시 1회) — 결과로 Step2 이후 흐름이 갈린다.
    //   조회 실패·미적용이면 기존 흐름 그대로 (policyEnabled=false 기본값).
    const setSignUpData = useSetAtom(signUpDataAtom);
    const policyState = useAtomValue(userStore.signupPolicy.stateAtom);
    useEffect(() => {
        if (redirectToSignIn) return;
        const { serviceId, countryCd } = getDefaultStore().get(signUpDataAtom);
        userStore.onGetSignupPolicy({ serviceId, countryCd: countryCd || undefined });
    }, [redirectToSignIn]);
    useEffect(() => {
        if (policyState.status !== "ready") return;
        // 생년월일 판정(Step2 다음) 이후에는 그 결과가 우선 — 늦게 도착한 진입 조회로 흐름을 바꾸지 않는다
        if (getDefaultStore().get(stepAtom) > 2) return;
        const data = policyState.data || {};
        setSignUpData(prev => ({
            ...prev,
            policyEnabled: data.policyEnabled === "Y",
            idvEnabled: data.idvEnabled !== "N",
        }));
    }, [policyState.status, policyState.data, setSignUpData]);

    // 뒤로가기 버튼을 숨길 step들 (결과 페이지들)
    const hideBackBtnSteps = [7, 8, 9, 10];

    // 헤더 뒤로 버튼 + device 뒤로 공통 핸들러.
    //   Step6 은 본인인증 통과 후 계정정보 입력 단계 — 이탈 시 인증·입력값 모두 잃으므로 팝업으로 확인.
    //   그 외 step 은 history.back() → popstate 핸들러가 stepStack pop / dead-end 처리.
    const handleBack = () => {
        if (step === 6) {
            setAbortOpen(true);
            return;
        }
        if (step === 7 || step === 8 || step === 9 || step === 10) {
            // dead-end (중복가입/실패/완료 안내) — 명시적 CTA 버튼만 허용.
            //   device back 으로 의도치 않게 /signin 으로 빠지는 것 방지 + 사용자에게 next action 안내.
            requestApp(WEB_TO_APP.SHOW_TOAST, { title: t("common.deadEndToast") });
            return;
        }
        window.history.back();
    };

    // device 뒤로 가로채기 — 네이티브가 PRESS_BACK 메시지로 위임. webview history 의존 없음.
    useBackInterceptor(handleBack);

    // Step6 이탈 확정 → /signin 으로 빠짐. Step7/8/9 dead-end 와 동일 패턴.
    const exitSignUp = () => {
        setSignUpExiting(true);
        window.history.go(-(historyDepth + 1));
    };

    // Step7 → /signin 이동 후 device back 으로 돌아온 경우 — /sign-up 의 모든 history entry pop 후 /signin 직행
    useEffect(() => {
        if (redirectToSignIn) {
            navigate(-(historyDepth + 1));
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectToSignIn, navigate]);

    // 물리 뒤로가기(Android) / 브라우저 뒤로가기 / 헤더 뒤로 버튼 — 모두 popstate 로 수렴.
    //   popstate 1회 = stepStack pop 1회. stepStack 변경은 여기서만 일어남.
    useEffect(() => {
        if (redirectToSignIn) return;
        const onPopState = () => {
            // exit cascade 중에는 handler skip
            if (isExitingSignUp()) return;

            // closure 내 stale 값 대신 jotai store 에서 최신 step 읽기
            const store = getDefaultStore();
            const currentStep = store.get(stepAtom);

            // Step7(이미 가입)/Step8(가입 실패)/Step9(가입 완료) — dead-end 화면.
            //   회원가입 단계로 되돌아가는 건 부자연스러움 → /signin 으로 직행.
            //   (Step9 가 빠지면 device back 시 Step6 으로 갔다가 signup 'ready' useEffect 로
            //    다시 pushStep(9) 핑퐁 → 앱종료 토스트 발생)
            if (currentStep === 7 || currentStep === 8 || currentStep === 9 || currentStep === 10) {
                setSignUpExiting(true);
                window.history.go(-store.get(signUpHistoryDepthAtom));
                return;
            }

            // Step6 — device back 은 useBackInterceptor 가 메시지 기반으로 가로채므로 여기 도달 안 함.
            //   안전망: handleBack 이 history.back() 안 부르고 setAbortOpen 만 하므로 fallback 케이스도 사실상 없음.
            //   다만 외부 history 조작(예: 브라우저 환경)으로 도달 시 안전하게 팝업만 띄움.
            if (currentStep === 6) {
                setAbortOpen(true);
                return;
            }

            const newStep = popStep();
            if (newStep === null) {
                // Step1 에서 뒤로 — 이미 history.back() 으로 /signin 이동 중. 추가 동작 없음.
                return;
            }
            if (newStep === 5) {
                // OTP/다날 인증 화면은 스킵 — history.back() 으로 다음 popstate 유발,
                //   그 popstate 가 일반 경로로 pop 1회 담당 (직접 popStep 호출 안 함).
                window.history.back();
            }
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [popStep, redirectToSignIn]);

    // 스텝 전환 시 스크롤 초기화
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    // 키보드 대응은 routers/WebviewProvider 의 useKeyboardInset 이 전 화면 공통으로 처리한다.

    if (redirectToSignIn) return null;

    return (
        <div className="sign-up-wrapper">
            <Header
                title={t('signUpText')}
                leftBtn={hideBackBtnSteps.includes(step) ? undefined : {
                    svg: Svgs.IcBack24,
                    func: handleBack
                }}
            />
            {/* Step 01 : 약관 */}
            {step === 1 && <SignUpStep1 />}
            {/* Step 02 : 생년월일 + 성별 (14세 미만 부모동의) */}
            {/*
                해외 이용자 -> step4
                국내 이용자 14세 미만 -> step3 분기 userStore: checkUnder14 True
                국내 이용자 14세 이상 -> step3 분기 userStore: checkUnder14 False
            */}
            {step === 2 && <SignUpStep2 />}

            {/* Step 03 : 다날 인증 정보 입력 (국내) */}
            {/* userStore: checkUnder14 True면 법정 대리인 인증 (정보 저장 안함) */}
            {/* userStore: checkUnder14 False면 본인 인증 */}
            {/* 이름, 통신사, 전화번호 */}
            {/* 완료 후 Step5 (OTP 인증)로 이동 */}
            {step === 3 && <SignUpStep3 />}
            {/* Step 04 : SMS 인증 정보 입력 (해외) */}
            {/* 국가코드, 전화번호 */}
            {/* 완료 후 Step5 (OTP 인증)로 이동 */}
            {step === 4 && <SignUpStep4 />}

            {/* Step 05 : OTP 인증 (Step3 또는 Step4에서 입력한 정보로 인증) */}
            {/* 아이디 전화번호 중복 체크 후 존재 시 Step7으로 이동 */}
            {/* 완료 후 Step6 (이메일/비밀번호 입력)으로 이동 */}
            {step === 5 && <SignUpStep5 />}

            {/* Step6 : 이메일 비밀번호 닉네임 입력 */}
            {step === 6 && <SignUpStep6 />}
            {/*
                step6의 결과에 따라 다음 step으로 이동
                step7 - 실패 : 중복 회원가입
                step8 - 실패 : 알 수 없는 이유
                step9 - 성공 : 회원가입 성공
            */}
            {step === 7 && <SignUpStep7 />}
            {step === 8 && <SignUpStep8 />}
            {step === 9 && <SignUpStep9 />}

            {/* 가입 연령 정책 (서버 판정) */}
            {/* Step10 : 가입 불가 안내 (dead-end) */}
            {step === 10 && <SignUpStep10 />}
            {/* Step11 : 본인 이름 + 실정보 확인 (+ 법정대리인 동의 대상이면 보호자 이름·관계) */}
            {step === 11 && <SignUpStep11 />}
            {/* Step12 : 보호자 휴대전화 번호 */}
            {step === 12 && <SignUpStep12 />}

            <ConfirmPopup
                isOpen={abortOpen}
                setIsOpen={setAbortOpen}
                title={t("signUp.abortPopup.title")}
                message={t("signUp.abortPopup.message")}
                leftBtnText={t("signUp.abortPopup.exit")}
                onLeft={() => { setAbortOpen(false); exitSignUp(); }}
                rightBtnText={t("signUp.abortPopup.continue")}
                onRight={() => setAbortOpen(false)}
                defaultFocus="right"
            />
        </div>
    )
}
